/**
 * Tiny reactive cache for the console's polled data.
 *
 * Every console widget polls one or more endpoints at its own cadence.
 * Without coordination we'd get N renders of the same fetch. The store
 * dedupes: each endpoint is fetched exactly once per cadence interval,
 * cached, and broadcast to all subscribers.
 *
 * Phase 1 scope: simple polling, no retry/backoff (the worker is
 * fast + cached). Phase 5 swaps the polling for Cloudflare Durable
 * Object WebSockets.
 */
import { api } from "./api";

export interface CacheEntry<T> {
  data:        T | null;
  loading:     boolean;
  error:       Error | null;
  fetched_at:  number;          // ms epoch
  generation:  number;          // bumped on every update — for memoization
}

type EndpointKey =
  | "whoami"
  | "macStatus"
  | "vpsStatus"
  | "workersStatus"
  | "anthropicSpend"
  | "todaysSlate"
  | "anomalies"
  | "activity"
  | "schedule"
  | "pipelineGraph"
  | "calibration"
  | "calibrationHistory"
  | "bets"
  | "costSpend"
  | "promptsIndex"
  | "snapshotsIndex"
  | "hrRotation"
  | "anomalyEvents"
  | "todos"
  | "morningBrief"
  | "eggEvents"
  | "activeEggs";

const CADENCES: Record<EndpointKey, number> = {
  whoami:         5 * 60_000,
  macStatus:      30_000,
  vpsStatus:      30_000,
  workersStatus:  30_000,
  anthropicSpend: 5 * 60_000,
  todaysSlate:    30_000,
  anomalies:      30_000,
  activity:       30_000,
  schedule:       60_000,
  pipelineGraph:  30_000,
  calibration:    5 * 60_000,
  calibrationHistory: 15 * 60_000,
  bets:           60_000,
  costSpend:      30_000,
  promptsIndex:   2 * 60_000,
  snapshotsIndex: 5 * 60_000,
  hrRotation:     5 * 60_000,
  anomalyEvents:  60_000,
  todos:          5 * 60_000,
  morningBrief:   5 * 60_000,
  eggEvents:      2 * 60_000,
  activeEggs:     30_000,
};

const FETCHERS: Record<EndpointKey, () => Promise<unknown>> = {
  whoami:         () => api.whoami(),
  macStatus:      () => api.macStatus(),
  vpsStatus:      () => api.vpsStatus(),
  workersStatus:  () => api.workersStatus(),
  anthropicSpend: () => api.anthropicSpend(),
  todaysSlate:    () => api.todaysSlate(),
  anomalies:      () => api.anomalies(),
  activity:       () => api.activity(50),
  schedule:       () => api.schedule(),
  pipelineGraph:  () => api.pipelineGraph(),
  calibration:    () => api.calibration(),
  calibrationHistory: () => api.calibrationHistory(30),
  bets:           () => api.bets(1000),
  costSpend:      () => api.costSpend(30),
  promptsIndex:   () => api.promptsIndex(),
  snapshotsIndex: () => api.snapshotsIndex(),
  hrRotation:     () => api.hrRotation(),
  anomalyEvents:  () => api.anomalyEvents(200),
  todos:          () => api.todos(),
  morningBrief:   () => api.morningBrief(),
  eggEvents:      () => api.eggEvents(),
  activeEggs:     () => api.activeEggs(),
};

class Store {
  private cache = new Map<EndpointKey, CacheEntry<unknown>>();
  private subscribers = new Set<() => void>();
  private timers = new Map<EndpointKey, ReturnType<typeof setTimeout>>();
  private generation = 0;

  subscribe(fn: () => void): () => void {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  get<T>(key: EndpointKey): CacheEntry<T> {
    return (this.cache.get(key) as CacheEntry<T>) ?? {
      data: null, loading: false, error: null, fetched_at: 0, generation: 0,
    };
  }

  /** Kick off all polling loops. Each endpoint refetches at its own cadence. */
  start(): void {
    for (const k of Object.keys(CADENCES) as EndpointKey[]) {
      this.scheduleNext(k, 0);  // immediate first fetch
    }
  }

  /** Force-refetch a single endpoint (e.g., after a manual user action). */
  refetch(key: EndpointKey): void {
    this.fetch(key);
  }

  // ----- internals --------------------------------------------------------
  private scheduleNext(key: EndpointKey, delayMs: number): void {
    const existing = this.timers.get(key);
    if (existing) clearTimeout(existing);
    this.timers.set(
      key,
      setTimeout(() => {
        this.fetch(key);
      }, delayMs),
    );
  }

  private async fetch(key: EndpointKey): Promise<void> {
    const cur = this.get(key);
    if (cur.loading) {
      this.scheduleNext(key, CADENCES[key]);
      return;
    }
    this.cache.set(key, { ...cur, loading: true });
    this.notify();
    let errored = false;
    try {
      const data = await FETCHERS[key]();
      this.cache.set(key, {
        data,
        loading: false,
        error: null,
        fetched_at: Date.now(),
        generation: ++this.generation,
      });
    } catch (e) {
      errored = true;
      this.cache.set(key, {
        data: cur.data,
        loading: false,
        error: e instanceof Error ? e : new Error(String(e)),
        fetched_at: cur.fetched_at,
        generation: ++this.generation,
      });
    }
    this.notify();
    // Self-heal: errored endpoints retry in 15s instead of waiting the
    // full cadence — important for whoami (5-min cadence) so transient
    // edge propagation issues clear quickly.
    this.scheduleNext(key, errored ? 15_000 : CADENCES[key]);
  }

  private notify(): void {
    this.subscribers.forEach((fn) => {
      try { fn(); } catch (e) { console.error("subscriber threw:", e); }
    });
  }
}

export const store = new Store();
